/**
 * 提示词管理 - 商家信息 / 知识文档 / 占位符 接口
 * 占位符已按定稿文档对齐（RESTful）；商家信息 / 知识文档 后端未就绪，端点为猜测命名，待联调对齐
 */
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import { apiGet, apiPost, apiPut, apiRequest, apiDownload } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import type {
  DocType,
  KnowledgeDocParseResult,
  KnowledgeDocCreatePayload,
  KnowledgeDocCreateResult,
  KnowledgeDocSimilarItem,
  KnowledgeDocUpdatePayload,
} from '@/types/robotPrompt';
import type {
  PromptProcessGroup,
  PromptProcessRow,
  PromptProcessSaveBody,
  PromptProcessSavePayload,
  PromptTemplatesData,
} from '@/types/dialogueConfig';

/** 分页加载兜底：接口未就绪/失败时静默返回空列表，避免拦截器弹错 */
async function safePaginated(p: Promise<unknown>): Promise<{ list: unknown[]; total: number }> {
  try {
    return parsePaginatedResponse(await p);
  } catch {
    return { list: [], total: 0 };
  }
}

/** 商家信息（后端资源为 knowledge-bases，RESTful） */
export const merchantsApi = {
  list: (params: Record<string, unknown> = {}) =>
    safePaginated(apiGet('/knowledge-bases', { per_page: DEFAULT_PAGE_SIZE, ...params })),
  detail: (id: number, versionType = 0) =>
    apiGet(`/knowledge-bases/${id}`, { version_type: versionType }),
  create: (data: Record<string, unknown>) => apiPost('/knowledge-bases', data),
  update: (id: number, data: Record<string, unknown>) => apiPut(`/knowledge-bases/${id}`, data),
  updateStatus: (id: number, status: number) => apiPut(`/knowledge-bases/${id}`, { status }),
  delete: (id: number) => apiRequest(`/knowledge-bases/${id}`, { method: 'DELETE' }),
  import: (data: FormData) => apiRequest('/knowledge-bases/import', { method: 'POST', data }),
  downloadTemplate: () =>
    apiDownload('/knowledge-bases/download-template', { filename: '商家信息导入模板.xlsx' }),
};

/** 商家信息「校准车型」品牌下拉选项：无入参、全局共用，旧库未配置时返回空数组 */
export const queryCarModelBranches = () => apiGet<string[]>('/car-models/branches');

/** 知识文档（RESTful /knowledge-documents） */
export const knowledgeDocsApi = {
  list: (params: Record<string, unknown> = {}) =>
    safePaginated(apiGet('/knowledge-documents', { per_page: DEFAULT_PAGE_SIZE, ...params })),
  /** 详情：含 segments 数组，供编辑弹窗解析展示知识条目 */
  detail: (id: number, versionType = 0) =>
    apiGet(`/knowledge-documents/${id}`, { version_type: versionType }),
  /** 解析文件内容（预览）：multipart（doc_type / file），返回 entries + name，不入库 */
  parseContent: (data: FormData) =>
    apiPost<KnowledgeDocParseResult>('/knowledge-documents/parse-content', data),
  /** 解析相似问（预览）：multipart（robot_id / document_id? / file），按标题匹配返回 entries_id + query_text，不入库 */
  parseSimilarQuestions: (data: FormData) =>
    apiPost<KnowledgeDocSimilarItem[]>('/knowledge-documents/parse-similar-questions', data),
  /** 创建文档：JSON（robot_id / doc_type / name / version_type / entries），返回 item + skipped_titles */
  create: (data: KnowledgeDocCreatePayload) =>
    apiPost<KnowledgeDocCreateResult>('/knowledge-documents', data),
  /** 编辑：JSON（name / channel_key / status / account_ids / child_entries）；文件替换走 update-file */
  update: (id: number, data: KnowledgeDocUpdatePayload) =>
    apiPut(`/knowledge-documents/${id}`, data),
  /** 上传文件替换文档内容：multipart（仅 file），后端按 doc_type 重新解析 */
  updateFile: (id: number, data: FormData) =>
    apiRequest(`/knowledge-documents/${id}/update-file`, { method: 'POST', data }),
  /** 复制（含 segments + bindings，新名追加 _副本） */
  copy: (id: number) => apiPost(`/knowledge-documents/${id}/copy`),
  /** 启用/禁用切换（无参纯切换） */
  toggle: (id: number) => apiPost(`/knowledge-documents/${id}/toggle`),
  /** 重新向量化（对 vector_status=0 的文档重新发起，含文档内容 + 相似问） */
  revectorize: (id: number) =>
    apiPost<{
      doc_id: number;
      doc_vectorized: boolean;
      doc_vectorized_count: number;
      child_vectorized: boolean;
      child_vectorized_count: number;
    }>(`/knowledge-documents/${id}/re-vectorize`),
  delete: (id: number) => apiRequest(`/knowledge-documents/${id}`, { method: 'DELETE' }),
  /** 下载原文件（二进制流；Content-Disposition 多为乱码且跨域未暴露，文件名由调用方用文档名传入） */
  download: (id: number, filename?: string) =>
    apiDownload(`/knowledge-documents/${id}/download`, { filename }),
  /** 按文档类型下载导入模板（二进制流） */
  downloadTemplate: (docType: DocType) =>
    apiDownload(`/knowledge-documents/download-template?doc_type=${docType}`, {
      filename: docType === 1 ? '知识Word文档导入.docx' : '知识Excel文档导入.xlsx',
    }),
  /** 下载相似问导入模板（二进制流；两列：标题 / 相似问） */
  downloadSimilarTemplate: () =>
    apiDownload('/knowledge-documents/download-questions-template', {
      filename: '相似问导入模板.xlsx',
    }),
};

/** 占位符（RESTful） */
export const placeholdersApi = {
  /** 内容类型枚举（动态获取，前端不写死） */
  types: () => apiGet('/robot-placeholders/types'),
  /** 选择列表（无分页，下拉/插入占位符用） */
  selectList: (robotId: number, versionType = 0) =>
    apiGet('/robot-placeholders/select-list', { robot_id: robotId, version_type: versionType }),
  list: (params: Record<string, unknown> = {}) =>
    safePaginated(apiGet('/robot-placeholders', { per_page: DEFAULT_PAGE_SIZE, ...params })),
  create: (data: Record<string, unknown>) => apiPost('/robot-placeholders', data),
  update: (id: number, data: Record<string, unknown>) => apiPut(`/robot-placeholders/${id}`, data),
  delete: (id: number) => apiRequest(`/robot-placeholders/${id}`, { method: 'DELETE' }),
};

/** 对话要求配置 - 提示词条目（按流程分组，批量保存：新增/更新/删除合一） */
export const promptItemsApi = {
  /** 获取条目列表（系统流程 + 当前机器人自定义流程合并返回，按流程分组） */
  list: (robotId: number, versionType = 0) =>
    apiGet<PromptProcessGroup[]>('/robot-prompt-items', {
      robot_id: robotId,
      version_type: versionType,
    }),
  /** 批量保存（一次性提交全部流程，某流程 items 为空数组表示清空该流程配置） */
  save: (robotId: number, versionType: number, items: PromptProcessSavePayload[]) =>
    apiPost<PromptProcessGroup[]>('/robot-prompt-items/save', {
      robot_id: robotId,
      version_type: versionType,
      items,
    }),
  /** 使用模板：按分套分组的模板数据（pre_lead / post_lead） */
  templates: (robotId: number) =>
    apiGet<PromptTemplatesData>('/robot-prompt-items/templates', {
      robot_id: robotId,
    }),
};

/** 对话流程（系统流程 robot_id=0 只读，自定义流程可增删改） */
export const promptProcessesApi = {
  list: (robotId: number, versionType = 0) =>
    apiGet<PromptProcessRow[]>('/robot-prompt-items/processes', {
      robot_id: robotId,
      version_type: versionType,
    }),
  create: (data: PromptProcessSaveBody) =>
    apiPost<PromptProcessRow>('/robot-prompt-items/processes', data),
  update: (id: number, data: PromptProcessSaveBody) =>
    apiPut<PromptProcessRow>(`/robot-prompt-items/processes/${id}`, data),
  delete: (id: number) => apiRequest(`/robot-prompt-items/processes/${id}`, { method: 'DELETE' }),
};
